/**
 * SessionRegister — the Enrollments register on the session page (11E.7).
 *
 * Owns the roster read and the attendance write (both Phase 5). It does
 * NOT own enrolment: enrolling a student means creating a booking, and
 * `features/attendance` may not import `features/bookings`. So `onEnroll`
 * / `onUnenroll` / `renderStudentPicker` are injected from the composition
 * root, which is allowed to know about both.
 *
 * Per D8 this is Enrollments only — Trials and Waitlist tabs are recorded
 * in the 11A.8 deferred-entity backlog, not built here.
 *
 * Marking is idempotent server-side (deterministic `{session}__{student}`
 * doc ids, equal-status rows short-circuit before any write), so a repeat
 * Save is safe and the operator is never blocked from re-submitting.
 */

import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';

import { Button, ConfirmDialog } from '@/components/common';
import { ApiError } from '@core/api/client';
import {
  useMarkAttendance,
  useSessionAttendance,
  type AttendanceEntryRequest,
  type AttendanceStatus,
} from '@features/attendance/api/attendance';
import { ATTENDANCE_STATUS_OPTIONS } from '@features/attendance/config/ui';

export interface SessionRegisterProps {
  sessionId: string;
  /** Disables every write control (cancelled session). */
  readOnly?: boolean;
  onEnroll?: (studentId: string) => Promise<void>;
  onUnenroll?: (studentId: string) => Promise<void>;
  /** Injected searchable student picker; calls back with the chosen id. */
  renderStudentPicker?: (onPick: (studentId: string) => void) => React.ReactNode;
}

type SortKey = 'name' | 'status';

interface Row {
  studentId: string;
  studentName: string | null;
  status: AttendanceStatus | '';
  serverStatus: AttendanceStatus | '';
}

export const SessionRegister: React.FC<SessionRegisterProps> = ({
  sessionId,
  readOnly = false,
  onEnroll,
  onUnenroll,
  renderStudentPicker,
}) => {
  const { data: roster, isLoading, isError, refetch } = useSessionAttendance(sessionId);
  const markMut = useMarkAttendance(sessionId);

  const [rows, setRows] = useState<Row[]>([]);
  const [selected, setSelected] = useState<string[]>([]);
  const [sortKey, setSortKey] = useState<SortKey>('name');
  const [autoPresent, setAutoPresent] = useState(false);
  const [confirmUnenroll, setConfirmUnenroll] = useState(false);
  const [unenrolling, setUnenrolling] = useState(false);

  // Re-seed local edit state when the server roster MEANINGFULLY changes.
  //
  // Keying this on the `roster` object identity meant any background
  // react-query refetch — window focus, or the invalidation fired by
  // enrolling a student — replaced every row and silently discarded
  // attendance the operator had entered but not yet saved. Comparing a
  // stable signature instead means a refetch returning the same roster and
  // the same server-side statuses is a no-op, so local edits survive it.
  const rosterSignature = useMemo(
    () =>
      (roster?.entries ?? [])
        .map((e) => `${e.student_id}:${e.attendance?.status ?? ''}`)
        .join('|'),
    [roster],
  );

  useEffect(() => {
    if (!roster) return;
    setRows((current) => {
      const localByStudent = new Map(current.map((r) => [r.studentId, r]));
      return roster.entries.map((e) => {
        const serverStatus = e.attendance?.status ?? '';
        const local = localByStudent.get(e.student_id);
        // Keep an unsaved local choice; otherwise take the server's.
        const keepLocal = local && local.status !== local.serverStatus;
        return {
          studentId: e.student_id,
          studentName: e.student_name ?? null,
          status: keepLocal ? local.status : serverStatus,
          serverStatus,
        };
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rosterSignature]);

  const sortedRows = useMemo(() => {
    const copy = [...rows];
    copy.sort((a, b) =>
      sortKey === 'name'
        ? (a.studentName ?? a.studentId).localeCompare(b.studentName ?? b.studentId)
        : (a.status || 'zz').localeCompare(b.status || 'zz'),
    );
    return copy;
  }, [rows, sortKey]);

  const setStatus = (studentId: string, status: AttendanceStatus | '') =>
    setRows((cur) => cur.map((r) => (r.studentId === studentId ? { ...r, status } : r)));

  const dirty = rows.filter((r) => r.status && r.status !== r.serverStatus);

  const handleSave = async () => {
    // "Mark all present automatically" fills only the rows the operator
    // left blank — it must never overwrite a status they chose by hand.
    const effective: Row[] = autoPresent
      ? rows.map((r) => (r.status ? r : { ...r, status: 'present' as AttendanceStatus }))
      : rows;

    const entries: AttendanceEntryRequest[] = effective
      .filter((r) => r.status && r.status !== r.serverStatus)
      .map((r) => ({ student_id: r.studentId, status: r.status as AttendanceStatus }));

    if (entries.length === 0) {
      toast('Nothing to save — no attendance changed.');
      return;
    }

    try {
      const res = await markMut.mutateAsync(entries);
      if (res.failures > 0) {
        toast.error(`${res.successes} saved, ${res.failures} failed.`);
      } else {
        toast.success(`Attendance marked for ${res.successes} student(s).`);
      }
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Could not mark attendance');
    }
  };

  const handleBulkUnenroll = async () => {
    if (!onUnenroll || selected.length === 0) return;
    // Snapshot the batch: `selected` is cleared below, and the dialog's own
    // title reads off it while this is still in flight.
    const batch = selected;
    setUnenrolling(true);
    let ok = 0;
    try {
      for (const id of batch) {
        try {
          await onUnenroll(id);
          ok += 1;
        } catch {
          // Per-student failure must not abort the rest of the batch.
        }
      }
      setSelected([]);
      toast[ok === batch.length ? 'success' : 'error'](`Removed ${ok} of ${batch.length}.`);
      void refetch();
    } finally {
      // ConfirmDialog is fully controlled — it never closes itself.
      setUnenrolling(false);
      setConfirmUnenroll(false);
    }
  };

  if (isLoading) {
    return <div data-testid="candeza-register-loading">Loading register…</div>;
  }
  if (isError) {
    return (
      <div data-testid="candeza-register-error" style={{ color: '#dc2626' }}>
        Failed to load the register.{' '}
        <Button size="sm" variant="outline" onClick={() => refetch()}>
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div data-testid="candeza-register">
      {!readOnly && renderStudentPicker && onEnroll && (
        <div style={{ marginBottom: 12 }}>
          {renderStudentPicker((studentId) => {
            void onEnroll(studentId)
              .then(() => {
                toast.success('Student enrolled');
                void refetch();
              })
              .catch((e: unknown) =>
                toast.error(e instanceof ApiError ? e.message : 'Enrolment failed'),
              );
          })}
        </div>
      )}

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          marginBottom: 8,
          flexWrap: 'wrap',
        }}
      >
        <label style={{ fontSize: 13 }}>
          Sort by{' '}
          <select
            data-testid="candeza-register-sort"
            value={sortKey}
            onChange={(e) => setSortKey(e.target.value as SortKey)}
          >
            <option value="name">Name</option>
            <option value="status">Attendance status</option>
          </select>
        </label>
        <span style={{ fontSize: 13, color: '#64748b' }}>
          {rows.length} enrolled
        </span>
        {selected.length > 0 && !readOnly && onUnenroll && (
          <Button
            size="sm"
            variant="outline"
            data-testid="candeza-register-bulk-unenroll"
            onClick={() => setConfirmUnenroll(true)}
          >
            Remove {selected.length} selected
          </Button>
        )}
      </div>

      {/* Was window.confirm — 11E.7 replaced those on the session page and
          11F.2 shipped <ConfirmDialog>; this one was left behind. */}
      <ConfirmDialog
        isOpen={confirmUnenroll}
        onClose={() => {
          if (!unenrolling) setConfirmUnenroll(false);
        }}
        onConfirm={handleBulkUnenroll}
        busy={unenrolling}
        title={`Remove ${selected.length} student(s) from this session?`}
        description="They keep their entitlement — this only removes them from this session's roster."
        confirmLabel="Remove"
        destructive
        testIdPrefix="candeza-register-unenroll-confirm"
      />

      {rows.length === 0 ? (
        <div data-testid="candeza-register-empty" style={{ color: '#64748b', fontSize: 14 }}>
          No students enrolled in this session yet.
        </div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
          <thead>
            <tr style={{ textAlign: 'left', color: '#64748b', fontSize: 12 }}>
              <th style={{ padding: '6px 4px', width: 28 }} />
              <th style={{ padding: '6px 4px' }}>Student</th>
              <th style={{ padding: '6px 4px', width: 190 }}>Attendance</th>
              <th style={{ padding: '6px 4px', width: 90 }} />
            </tr>
          </thead>
          <tbody>
            {sortedRows.map((r) => (
              <tr
                key={r.studentId}
                data-testid={`candeza-register-row-${r.studentId}`}
                style={{ borderTop: '1px solid #e2e8f0' }}
              >
                <td style={{ padding: '6px 4px' }}>
                  <input
                    type="checkbox"
                    aria-label={`Select ${r.studentName ?? r.studentId}`}
                    data-testid={`candeza-register-select-${r.studentId}`}
                    checked={selected.includes(r.studentId)}
                    onChange={(e) =>
                      setSelected((cur) =>
                        e.target.checked
                          ? [...cur, r.studentId]
                          : cur.filter((x) => x !== r.studentId),
                      )
                    }
                  />
                </td>
                <td style={{ padding: '6px 4px' }}>{r.studentName ?? r.studentId}</td>
                <td style={{ padding: '6px 4px' }}>
                  <select
                    data-testid={`candeza-register-status-${r.studentId}`}
                    value={r.status}
                    disabled={readOnly}
                    onChange={(e) =>
                      setStatus(r.studentId, e.target.value as AttendanceStatus | '')
                    }
                    style={{ width: '100%', padding: 4 }}
                  >
                    <option value="">— not marked —</option>
                    {ATTENDANCE_STATUS_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </td>
                <td style={{ padding: '6px 4px' }}>
                  {!readOnly && onUnenroll && (
                    <Button
                      size="sm"
                      variant="outline"
                      data-testid={`candeza-register-unenroll-${r.studentId}`}
                      onClick={() => {
                        void onUnenroll(r.studentId)
                          .then(() => {
                            toast.success('Student removed');
                            void refetch();
                          })
                          .catch((e: unknown) =>
                            toast.error(
                              e instanceof ApiError ? e.message : 'Could not remove',
                            ),
                          );
                      }}
                    >
                      Remove
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {!readOnly && rows.length > 0 && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            marginTop: 14,
            flexWrap: 'wrap',
          }}
        >
          <label style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
            <input
              type="checkbox"
              data-testid="candeza-register-mark-all-present"
              checked={autoPresent}
              onChange={(e) => setAutoPresent(e.target.checked)}
            />
            Mark all present automatically
          </label>
          <Button
            data-testid="candeza-register-save"
            onClick={() => void handleSave()}
            disabled={markMut.isPending || (!autoPresent && dirty.length === 0)}
          >
            {markMut.isPending ? 'Saving…' : 'Mark attendance'}
          </Button>
        </div>
      )}
    </div>
  );
};
